import React from "react";
import { ConversationComment } from "services/ConversationService";
import { UserWithFullName } from "components/Signals/Dashboard/DashboardMenu/ShareDialog/constants";
import CommentThread from "./CommentThread";

type CommentListProps = {
    rootComments: ConversationComment[];
    repliesByParentId: Map<string, ConversationComment[]>;
    replyEditByParentId: Map<string, ConversationComment | null>;
    isChat: boolean;
    activeReplyParentId: string | null;
    activeEditCommentId: string | null;
    renderCommentContent: (
        comment: ConversationComment,
        options: { showTimestamp: boolean; editComposer?: React.ReactNode },
    ) => React.ReactNode;
    users: UserWithFullName[] | null | undefined;
    usersLoading: boolean;
    tokenizeMentions: (
        commentText: string,
        selections: UserWithFullName[],
    ) => string;
    detokenizeMentions: (commentText: string) => string;
    getTaggedUserIdsFromComment: (commentText: string) => string[];
    onAddReply: (
        parentComment: ConversationComment,
        commentText: string,
        taggedUserIds: string[],
    ) => Promise<void>;
    onSaveEdit: (
        commentId: string,
        commentText: string,
        taggedUserIds: string[],
    ) => Promise<void>;
    onCancelEdit: () => void;
    onReplyClick: (commentId: string) => void;
    onCancelReply: () => void;
};

const CommentList = React.memo(
    ({
        rootComments,
        repliesByParentId,
        replyEditByParentId,
        isChat,
        activeReplyParentId,
        activeEditCommentId,
        renderCommentContent,
        users,
        usersLoading,
        tokenizeMentions,
        detokenizeMentions,
        getTaggedUserIdsFromComment,
        onAddReply,
        onSaveEdit,
        onCancelEdit,
        onReplyClick,
        onCancelReply,
    }: CommentListProps) => {
        const commentListItems = React.useMemo(
            () =>
                rootComments.map((comment) => {
                    const replies = comment.id
                        ? repliesByParentId.get(comment.id) ?? []
                        : [];
                    const isReplying =
                        Boolean(comment.id) &&
                        comment.id === activeReplyParentId;
                    const isEditing =
                        Boolean(comment.id) &&
                        comment.id === activeEditCommentId;
                    const replyEditComment = comment.id
                        ? replyEditByParentId.get(comment.id) ?? null
                        : null;

                    return (
                        <CommentThread
                            key={comment.id}
                            comment={comment}
                            isChat={isChat}
                            replies={replies}
                            renderCommentContent={renderCommentContent}
                            isReplying={isReplying}
                            isEditing={isEditing}
                            replyEditComment={replyEditComment ?? null}
                            users={users}
                            usersLoading={usersLoading}
                            tokenizeMentions={tokenizeMentions}
                            detokenizeMentions={detokenizeMentions}
                            getTaggedUserIdsFromComment={
                                getTaggedUserIdsFromComment
                            }
                            onAddReply={onAddReply}
                            onSaveEdit={onSaveEdit}
                            onCancelEdit={onCancelEdit}
                            onReplyClick={onReplyClick}
                            onCancelReply={onCancelReply}
                        />
                    );
                }),
            [
                activeEditCommentId,
                activeReplyParentId,
                detokenizeMentions,
                getTaggedUserIdsFromComment,
                isChat,
                onAddReply,
                onCancelEdit,
                onCancelReply,
                onReplyClick,
                onSaveEdit,
                renderCommentContent,
                repliesByParentId,
                replyEditByParentId,
                rootComments,
                tokenizeMentions,
                users,
                usersLoading,
            ],
        );

        return <>{commentListItems}</>;
    },
);

export default CommentList;
